/**
 * Generisk share-modal — genbruges af Recipe- og GuideDetailPage.
 *
 * Adfærd matcher den oprindelige opskrifts-modal 1:1:
 *   - Link-boks + "Kopier link"
 *   - Facebook, WhatsApp, X/Twitter, Email, SMS
 *   - "Flere" (native share) på enheder der understøtter det
 *   - Reassurance-bullets: native share, offentligt link, tak-for-deling
 *
 * Kaldes IKKE direkte af komponenten der starter share — flowet er:
 *   1. Parent forsøger `navigator.share()` (native mobile share sheet)
 *   2. Hvis afvist eller ikke understøttet → åbn denne modal
 *
 * Props:
 *   open       (bool)               — kontrolleret synlighed
 *   onClose    (fn)                 — kaldes ved luk (X, ESC, backdrop, "Luk")
 *   shareUrl   (string)             — det fulde offentlige link der deles
 *   shareTitle (string)             — bruges i social-post title
 *   shareText  (string, valgfri)    — kort beskrivelse (fx til Email body)
 *   heading    (string)             — modal-overskrift, fx "Del opskriften"
 *   subheading (string, valgfri)    — hjælpetekst under overskrift
 *   testId     (string, valgfri)    — override for data-testid (default "share-modal")
 */
import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import {
  FaTimes, FaLink, FaCopy, FaCheck, FaFacebookF, FaWhatsapp,
  FaEnvelope, FaSms, FaEllipsisH, FaShareAlt, FaLock, FaHeart,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

function buildSocialUrls(url, title, text) {
  const safeTitle = title || '';
  const safeText = text || '';
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${safeTitle}\n${url}`)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(safeTitle)}`,
    email: `mailto:?subject=${encodeURIComponent(safeTitle)}&body=${encodeURIComponent(`${safeText}\n\n${url}`)}`,
    sms: `sms:?&body=${encodeURIComponent(`${safeTitle} ${url}`)}`,
  };
}

const SocialIcon = ({ icon: Ic, bg, testid, href, target, onClick, label }) => {
  const inner = (
    <>
      <div className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center text-white shadow-sm hover:scale-105 active:scale-95 transition-transform`}>
        <Ic size={22} />
      </div>
      <span className="text-xs text-gray-700 mt-1.5">{label}</span>
    </>
  );
  if (href) {
    return (
      <a href={href} target={target} rel="noopener noreferrer" data-testid={testid} className="flex flex-col items-center">
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="flex flex-col items-center">
      {inner}
    </button>
  );
};

export const ShareModal = ({
  open,
  onClose,
  shareUrl,
  shareTitle,
  shareText = '',
  heading = 'Del',
  subheading = 'Del dette link med venner og familie. Modtagere kan åbne det uden at logge ind.',
  testId = 'share-modal',
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  // ESC-luk — matcher forventet modal-adfærd.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Nulstil copied-state når modalen lukker/åbner.
  useEffect(() => {
    if (!open) setLinkCopied(false);
  }, [open]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success('Link kopieret!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Kunne ikke kopiere automatisk — vælg linket og kopiér manuelt.');
    }
  }, [shareUrl]);

  if (!open) return null;

  const urls = buildSocialUrls(shareUrl, shareTitle, shareText);
  const supportsNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid={testId}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl my-8">
        <div className="flex justify-between items-start mb-4">
          <img src="/slushbook-nav-logo.png" alt="SlushBook" className="h-12 w-auto" />
          <button
            type="button"
            onClick={onClose}
            data-testid="share-modal-close"
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Luk"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="text-center mb-5">
          <h3 className="text-2xl font-bold text-gray-900 mb-1">{heading}</h3>
          <p className="text-sm text-gray-600">{subheading}</p>
        </div>

        <div className="mb-5">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2 mb-2">
            <FaLink className="text-gray-400 flex-shrink-0" size={14} />
            <input
              type="text"
              value={shareUrl}
              readOnly
              data-testid="share-link-input"
              className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
              onFocus={(e) => e.target.select()}
            />
          </div>
          <button
            type="button"
            onClick={copy}
            data-testid="share-copy-link"
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors border ${
              linkCopied
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {linkCopied ? (<><FaCheck size={14} /> Kopieret!</>) : (<><FaCopy size={14} /> Kopier link</>)}
          </button>
        </div>

        <div className="mb-5">
          <h4 className="text-sm font-bold text-gray-800 mb-3">Del via</h4>
          <div className="grid grid-cols-4 gap-3">
            <SocialIcon icon={FaFacebookF} bg="bg-[#1877F2]" testid="share-facebook" href={urls.facebook} target="_blank" label="Facebook" />
            <SocialIcon icon={FaWhatsapp}  bg="bg-[#25D366]" testid="share-whatsapp" href={urls.whatsapp} target="_blank" label="WhatsApp" />
            <SocialIcon icon={FaXTwitter}  bg="bg-black"     testid="share-twitter"  href={urls.twitter}  target="_blank" label="X / Twitter" />
            <SocialIcon icon={FaEnvelope}  bg="bg-[#3B82F6]" testid="share-email"    href={urls.email}    target="_self"  label="Email" />
            <SocialIcon icon={FaSms}       bg="bg-[#22C55E]" testid="share-sms"      href={urls.sms}      target="_self"  label="SMS" />
            {supportsNativeShare && (
              <SocialIcon
                icon={FaEllipsisH}
                bg="bg-gray-400"
                testid="share-more"
                label="Flere"
                onClick={async () => {
                  try {
                    await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
                    onClose?.();
                  } catch (err) {
                    if (err && err.name !== 'AbortError') console.warn('navigator.share failed:', err);
                  }
                }}
              />
            )}
          </div>
        </div>

        <div className="space-y-3 mb-5 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
              <FaShareAlt size={14} />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Native deling på mobil</div>
              <div className="text-gray-600 text-xs">Vi åbner din enheds delingsmenu automatisk.</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-green-600 flex-shrink-0">
              <FaLock size={14} />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Offentligt link</div>
              <div className="text-gray-600 text-xs">Alle kan se indholdet – ingen login nødvendig.</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center text-pink-500 flex-shrink-0">
              <FaHeart size={14} />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Tak fordi du deler!</div>
              <div className="text-gray-600 text-xs">Dine delinger hjælper os med at vokse 💗</div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-gray-800"
        >
          Luk
        </button>
      </div>
    </div>
  );
};

export default ShareModal;
